import { DocumentationArea } from "src/contexts/documentationArea/domain/DocumentationArea";
import { IdNumberCountries } from "src/contexts/documentationArea/domain/IdNumberCountries";
import { IdNumberTypes } from "src/contexts/documentationArea/domain/IdNumberTypes";
import { ActorPrefilledData } from "src/contexts/documentationArea/domain/ActorPrefilledData";
import { DocumentationAreaActorPrefilledData } from "src/contexts/documentationArea/domain/DocumentationAreaActorBasicData";

export const useRepresentedEntityFormData = (
  documentationArea: DocumentationArea,
  fallbackPrefilledData?: ActorPrefilledData | DocumentationAreaActorPrefilledData,
) => {
  const actor = documentationArea.getActor();
  const identity = actor.getIdentity();
  const prefilledData = fallbackPrefilledData ?? actor.getPrefilledData();

  const representative = identity?.getRepresentative();

  const representedEntity = identity?.getRepresentedEntity();

  const prefilledRepresentative = prefilledData?.identity.representative;
  const prefilledRepresentedEntity = prefilledData?.identity?.representedEntity;

  const representativeIdNumberValue = representative?.getIdNumber()?.number;
  const hasRepresentativeIdNumberValue = representativeIdNumberValue !== undefined;

  const prefilledRepresentativeIdNumbers = prefilledRepresentative?.idNumbers ?? [];
  const prefilledRepresentativeIdNumber =
    prefilledRepresentativeIdNumbers.length > 0 ? prefilledRepresentativeIdNumbers[0] : null;

  const prefilledRepresentativeIdNumberType =
    (prefilledRepresentativeIdNumber?.type.value as IdNumberTypes) ?? IdNumberTypes.ID_CARD;
  const prefilledRepresentativeIdNumberCountry =
    (prefilledRepresentativeIdNumber?.data?.country?.value as IdNumberCountries) ??
    IdNumberCountries.ES;
  const initialIdNumber =
    representativeIdNumberValue ?? prefilledRepresentativeIdNumber?.data?.number?.value ?? "";

  const initialRepresentativeIdNumber = {
    type: prefilledRepresentativeIdNumberType,
    country: prefilledRepresentativeIdNumberCountry,
    value: initialIdNumber,
  };

  const representedEntityIdNumberValue = representedEntity?.getIdNumber()?.number;
  const hasRepresentedEntityIdNumberValue = representedEntityIdNumberValue !== undefined;

  const prefilledRepresentedEntityIdNumbers = prefilledRepresentedEntity?.idNumbers ?? [];
  const prefilledRepresentedEntityIdNumber =
    prefilledRepresentedEntityIdNumbers.length > 0 ? prefilledRepresentedEntityIdNumbers[0] : null;
  const prefilledRepresentedEntityIdNumberType =
    (prefilledRepresentedEntityIdNumber?.type.value as IdNumberTypes) ?? IdNumberTypes.ID_CARD;
  const prefilledRepresentedEntityIdNumberCountry =
    (prefilledRepresentedEntityIdNumber?.data?.country?.value as IdNumberCountries) ??
    IdNumberCountries.ES;
  const initialCompanyNif =
    representedEntityIdNumberValue ?? prefilledRepresentedEntityIdNumber?.data?.number?.value ?? "";

  const initialRepresentedEntityIdNumber = {
    type: prefilledRepresentedEntityIdNumberType,
    country: prefilledRepresentedEntityIdNumberCountry,
    value: initialCompanyNif,
  };

  const isRepresentativeIdNumberEditable =
    (!hasRepresentativeIdNumberValue && prefilledRepresentativeIdNumber?.data?.number?.editable) ??
    true;
  const isRepresentedEntityEditable =
    (!hasRepresentedEntityIdNumberValue &&
      prefilledRepresentedEntityIdNumber?.data?.number?.editable) ??
    true;

  const isRepresentativeRelationTypeEditable =
    (!representedEntity?.getIndividualRelationType() &&
      prefilledRepresentedEntity?.individualRelationType?.editable) ??
    true;
  const isCompanyNameEditable =
    (!representedEntity?.getLegalName() && prefilledRepresentedEntity?.legalName?.editable) ?? true;

  const initialCompanyName =
    representedEntity?.getLegalName() ?? prefilledRepresentedEntity?.legalName?.value ?? "";
  const initialRepresentativeRelationType =
    representedEntity?.getIndividualRelationType() ??
    prefilledRepresentedEntity?.individualRelationType?.value ??
    "";

  return {
    representative,
    initialRepresentativeIdNumber,
    initialRepresentedEntityIdNumber,
    isRepresentativeIdNumberEditable,
    isRepresentedEntityEditable,
    isRepresentativeRelationTypeEditable,
    isCompanyNameEditable,
    initialCompanyName,
    initialRepresentativeRelationType,
  };
};

export type RepresentedEntityFormData = ReturnType<typeof useRepresentedEntityFormData>;
